import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  Mutation,
  MutationRegisterAzureTenantArgs,
  Query,
  RegisterAzureTenantInput
} from 'graphql/generated/graphqlTypes';
import { REGISTER_AZURE_TENANT } from 'graphql/mutations';
import { GET_TENANT_REGISTRATION_INFO } from 'graphql/queries';
import { useEffect } from 'react';
import { setTenantRegistrationInfoAction } from 'state/actions/tenantRegistrationInfo';
import { tenantRegistrationInfoVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_TENANT_REGISTRATION_INFO_TYPED: TypedDocumentNode<Pick<Query, 'getTenantRegistrationInfo'>> =
  GET_TENANT_REGISTRATION_INFO;
const REGISTER_AZURE_TENANT_TYPED: TypedDocumentNode<
  Pick<Mutation, 'registerAzureTenant'>,
  MutationRegisterAzureTenantArgs
> = REGISTER_AZURE_TENANT;

const useTenantRegistrationInfo = ({ handleFetchError }: BaseHookProps) => {
  const tenantRegistrationInfo = useReactiveVar(tenantRegistrationInfoVar);

  const [getTenantRegistrationInfo, { loading, data, error }] = useLazyQuery(GET_TENANT_REGISTRATION_INFO_TYPED);

  useEffect(() => {
    if (data) setTenantRegistrationInfoAction(data.getTenantRegistrationInfo);
  }, [data]);

  useEffect(() => {
    if (error) handleFetchError('Error while fetching tenant registration info');
  }, [error]);

  useEffect(() => {
    getTenantRegistrationInfo();
  }, [getTenantRegistrationInfo]);

  const [registerAzureTenantMutation, { loading: registerAzureTenantLoading }] = useMutation(
    REGISTER_AZURE_TENANT_TYPED,
    {
      onError: (mutationError) => {
        handleFetchError('Error while registering tenant');
        console.error(mutationError);
      }
    }
  );

  const registerAzureTenant = async (input: RegisterAzureTenantInput) => {
    await registerAzureTenantMutation({
      variables: {
        input
      }
    });
  };

  return {
    tenantRegistrationInfo,
    loading,
    registerAzureTenant,
    registerAzureTenantLoading
  };
};

export default useTenantRegistrationInfo;
